import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Button} from 'primeng/button';
import {Fluid} from 'primeng/fluid';
import {MessageModule} from 'primeng/message';
import {
  AuthMode,
  CountryService,
  RegisterCreateProviderUserRequest,
  TokenProviderDataResponse,
} from '@frontend/shared';
import {
  CreateTenantRequest,
  ManagedProviderResponse,
  ProviderManagerControllerService,
  ProviderRegisterControllerService,
  RegisterTenantAndProviderUserRequest,
  ScopedTokenControllerService,
  TenantType,
} from '@frontend/shared-api';
import {FormFieldComponent} from '../../../shared/components/inputs/form-field/form-field.component';
import {FormValidationService} from '../../../shared/services/form-validation.service';
import {AuthService} from '../../../shared/services/auth.service';
import {ProviderTenantSelectionService} from '../../../shared/services/provider-tenant-selection.service';
import {catchError, filter, finalize, map, switchMap, take, tap} from 'rxjs/operators';
import {EMPTY, from, Observable} from 'rxjs';
import {APP_ROUTES} from '../../../app.routes';

const SALES_TAX_NUMBER_MAX_LENGTH = 255;

@Component({
  selector: 'app-provider-register',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    FormFieldComponent,
    Button,
    TranslocoPipe,
    Fluid,
    MessageModule,
  ],
  templateUrl: './provider-register.component.html',
})
export class ProviderRegisterComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly scopedTokenService = inject(ScopedTokenControllerService);
  private readonly providerRegisterService = inject(ProviderRegisterControllerService);
  private readonly providerManagerService = inject(ProviderManagerControllerService);
  private readonly providerTenantSelectionService = inject(ProviderTenantSelectionService);
  private readonly countryService = inject(CountryService);
  private readonly formValidationService = inject(FormValidationService);
  private readonly authService = inject(AuthService);
  private readonly translocoService = inject(TranslocoService);

  readonly availableCountries = this.countryService.allCountries();
  readonly providerData = signal<TokenProviderDataResponse | null>(null);
  readonly isSso = computed(() => this.providerData()?.authMode === AuthMode.SSO);
  readonly isExistingAccount = computed(() => this.providerData()?.existingAccount === true);
  readonly isAuthenticated = signal(this.authService.isAuthenticated());
  readonly needsPassword = computed(() => !this.isSso() && !this.isExistingAccount());
  readonly loading = signal(false);
  readonly submitting = signal(false);
  readonly error = signal<string | null>(null);
  readonly errorKey = signal<string | null>(null);
  private token = '';

  readonly form = this.fb.group(
    {
      tenant: [{value: '', disabled: true}, Validators.required],
      email: [{value: '', disabled: true}, [Validators.required, Validators.email]],
      salesTaxNumber: [
        '',
        [Validators.required, Validators.maxLength(SALES_TAX_NUMBER_MAX_LENGTH)],
      ],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          this.formValidationService.passwordValidator(),
        ],
      ],
      passwordRepeat: ['', Validators.required],
    },
    {validators: this.formValidationService.passwordMatchValidator()},
  );

  ngOnInit(): void {
    this.loadTokenData();
  }

  private loadTokenData(): void {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('token') ?? ''),
        tap(this.checkToken.bind(this)),
        filter((token) => !!token),
        tap(() => this.setLoading(true)),
        switchMap((token) => this.fetchProviderData(token)),
        take(1),
        finalize(() => this.setLoading(false)),
      )
      .subscribe(this.onDataSuccess.bind(this));
  }

  private checkToken(token: string): void {
    if (!token) {
      this.setError('No token provided in URL', 'error.noToken');
    }
    this.token = token;
  }

  private fetchProviderData(token: string): Observable<TokenProviderDataResponse> {
    return this.scopedTokenService.getProviderData({token}).pipe(
      catchError((err) => {
        const key = err.error?.translationTitleKey || err.error?.translationKey || 'error.generic';
        this.setError(err.message || 'Failed to load provider data', key);
        return EMPTY;
      }),
    );
  }

  private onDataSuccess(data: TokenProviderDataResponse): void {
    this.providerData.set(data);
    this.form.patchValue({tenant: data.tenant, email: data.email});
    if (data.authMode === AuthMode.SSO || data.existingAccount) {
      this.form.get('password')?.disable();
      this.form.get('passwordRepeat')?.disable();
    }
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.markAllTouched();
      return;
    }

    const request = this.buildRegisterRequest();
    if (!request) {
      return;
    }

    this.setSubmitting(true);
    if (this.isExistingAccount()) {
      this.registerForExistingAccount(request);
      return;
    }
    this.providerRegisterService
      .createTenantAndProvider({registerTenantAndProviderUserRequest: request})
      .pipe(finalize(() => this.setSubmitting(false)))
      .subscribe({
        next: () => {
          this.router.navigate(
            [APP_ROUTES.REGISTRATION_SUCCESS],
            this.isSso() ? {queryParams: {sso: true}} : undefined,
          );
        },
        error: this.handleSubmitError.bind(this),
      });
  }

  private buildRegisterRequest(): RegisterTenantAndProviderUserRequest | null {
    const {salesTaxNumber, password} = this.form.value;
    const provider = this.providerData();
    if (!salesTaxNumber || !provider) {
      return null;
    }
    if (this.needsPassword() && !password) {
      return null;
    }

    return {
      tenant: {name: provider.tenant, tenantType: TenantType.PROVIDER} as CreateTenantRequest,
      user: {
        token: this.token,
        salesTaxNumber,
        languageCode: this.translocoService.getActiveLang(),
        ...(this.needsPassword() ? {password} : {}),
      } as RegisterCreateProviderUserRequest,
    };
  }

  private registerForExistingAccount(request: RegisterTenantAndProviderUserRequest): void {
    this.providerManagerService
      .registerManagedProvider({registerTenantAndProviderUserRequest: request})
      .pipe(
        switchMap(() => from(this.authService.forceTokenRefresh())),
        switchMap(() => this.providerManagerService.getManagedProviders()),
        finalize(() => this.setSubmitting(false)),
      )
      .subscribe({
        next: (providers) => this.openRegisteredProvider(providers, request.tenant.name),
        error: this.handleSubmitError.bind(this),
      });
  }

  private openRegisteredProvider(providers: ManagedProviderResponse[], tenantName: string): void {
    const registeredProvider = providers.find((provider) => provider.name === tenantName);
    if (registeredProvider) {
      this.providerTenantSelectionService.selectTenant(registeredProvider.tenantId);
    }
    window.location.assign(APP_ROUTES.PROVIDER_TENDERS);
  }

  private handleSubmitError(err: any): void {
    const key = err.error?.translationTitleKey || err.error?.translationKey || 'error.general';
    this.setError(err.message || 'Registration failed', key);
  }

  private markAllTouched(): void {
    Object.values(this.form.controls).forEach((ctrl) => ctrl.markAllAsTouched());
  }

  private setLoading(isLoading: boolean): void {
    this.loading.set(isLoading);
    if (isLoading) {
      this.error.set(null);
      this.errorKey.set(null);
    }
  }

  private setSubmitting(isSubmitting: boolean): void {
    this.submitting.set(isSubmitting);
    if (isSubmitting) {
      this.error.set(null);
      this.errorKey.set(null);
    }
  }

  private setError(message: string, key: string): void {
    this.error.set(message);
    this.errorKey.set(key);
    this.loading.set(false);
    this.submitting.set(false);
  }

  goToLogin(): void {
    this.authService.login(APP_ROUTES.PROVIDER);
  }

  loginWithExistingAccount(): void {
    this.authService.login(undefined, this.providerData()?.email);
  }
}
